import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import AnimatedText from '../components/AnimatedText';
import UnifiedFooter from '../components/UnifiedFooter';

const TermsPage: React.FC = () => {

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-24">
        <motion.div
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-8 space-y-8 transition-colors duration-300"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="space-y-4">
            <AnimatedText
              text="Terms of Use"
              className="text-3xl font-light text-gray-900 dark:text-gray-100 transition-colors duration-300"
              delay={0.2}
              staggerDelay={0.03}
            />
            <motion.p
              className="text-gray-600 dark:text-gray-400 transition-colors duration-300"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </motion.p>
          </div>

          <motion.section 
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">1. Introduction</h2>
            <p className="text-gray-600 dark:text-gray-300 transition-colors duration-300">
              Welcome to TTStats.ch ("we", "our", or "us"). By using our website (TTStats.ch) and our mobile application (TTStats), you agree to these Terms of Use. Please read them carefully.
            </p>
          </motion.section>

          <motion.section 
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">2. Account Registration and Authentication</h2>
            <div className="space-y-2">
              <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 transition-colors duration-300">2.1 Account Creation</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300 transition-colors duration-300">
                <li>To access certain features (favorites, My Profile, syncing, Pro features), you must create an account using a valid email address.</li>
                <li>You are responsible for maintaining the confidentiality of your account and for all activities that occur under your account.</li>
                <li>You must provide accurate and complete information when creating your account.</li>
                <li>One email address can only be associated with one account.</li>
              </ul>
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 transition-colors duration-300">2.2 PIN-Based Authentication</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300 transition-colors duration-300">
                <li>We use secure email-based authentication with 6-digit PIN codes instead of passwords.</li>
                <li>You will receive a one-time PIN code via email each time you sign in.</li>
                <li>PIN codes expire after a single use or after a short period for security purposes.</li>
                <li>You must have access to your registered email to use our services.</li>
                <li>Do not share your PIN codes with anyone.</li>
              </ul>
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 transition-colors duration-300">2.3 Account Deletion</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300 transition-colors duration-300">
                <li>You can permanently delete your account at any time through the user menu.</li>
                <li>Deleting your account will remove all your favorites, My Profile selection, avatar preferences, settings, and personal data.</li>
                <li>Account deletion is immediate and cannot be undone.</li>
                <li>If you have an active Pro subscription, you must cancel it separately through the App Store before deleting your account.</li>
              </ul>
            </div>
          </motion.section>

          <motion.section
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.75 }}
          >
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">3. My Profile Feature</h2>
            <div className="space-y-2">
              <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300 transition-colors duration-300">
                <li>You can link your account to your official Swiss Table Tennis player profile for quick access to your statistics.</li>
                <li>The "My Profile" feature allows you to select a personalized avatar from 9 available animated options.</li>
                <li>Your profile selection and avatar choice are saved to your account and synced across all devices.</li>
                <li>You can change your profile selection and avatar at any time through the profile settings.</li>
                <li>Profile data (name, club, ELO rating, classification) is sourced from the official Swiss Table Tennis database and displayed for your convenience.</li>
                <li>You are responsible for ensuring you link to the correct player profile.</li>
              </ul>
            </div>
          </motion.section>

          <motion.section
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">4. Cross-Platform Syncing</h2>
            <div className="space-y-2">
              <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300 transition-colors duration-300">
                <li>Your account syncs across the web application (TTStats.ch) and mobile applications (iOS and Android).</li>
                <li>Favorites, My Profile selection, avatar preferences, settings, and Pro subscription status are automatically synchronized across all platforms.</li>
                <li>You must use the same email address to sign in on all platforms to enable syncing.</li>
                <li>Syncing requires an active internet connection and may not be instantaneous.</li>
              </ul>
            </div>
          </motion.section>

          <motion.section
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.85 }}
          >
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">5. Subscriptions and Pro Features</h2>
            <div className="space-y-2">
              <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 transition-colors duration-300">5.1 Auto-Renewable Subscriptions</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300 transition-colors duration-300">
                <li>TTStats Pro is an auto-renewable subscription that provides access to advanced features and statistics.</li>
                <li>Subscription automatically renews unless auto-renew is turned off at least 24-hours before the end of the current period.</li>
                <li>Your account will be charged for renewal within 24-hours prior to the end of the current period.</li>
                <li>You can manage and turn off auto-renewal in your App Store Account Settings.</li>
              </ul>
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 transition-colors duration-300">5.2 Subscription Tiers and Pricing</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300 transition-colors duration-300">
                <li>Monthly Subscription: Provides access to all Pro features for one month</li>
                <li>Annual Subscription: Provides access to all Pro features for one year at a discounted rate</li>
                <li>Prices are clearly displayed in the app before purchase and may vary by region</li>
                <li>Any unused portion of a free trial period will be forfeited when purchasing a subscription</li>
              </ul>
            </div>
          </motion.section>

          <motion.section
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
          >
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">6. User Content and Conduct</h2>
            <p className="text-gray-600 dark:text-gray-300 transition-colors duration-300">
              Users are responsible for their conduct and any content they submit, including favorites, profile selections, and preferences. We reserve the right to remove any content that violates these terms.
            </p>
          </motion.section>

          <motion.section
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.0 }}
          >
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">7. Data Usage</h2>
            <p className="text-gray-600 dark:text-gray-300 transition-colors duration-300">
              We use table tennis data from the official Swiss Table Tennis database. This data is publicly available and we do not claim ownership of it. For more information on how we handle your personal data, please see our{' '}
              <Link to="/privacy" className="text-[#1C7AD6] hover:text-[#1565C0] transition-colors duration-300">
                Privacy Policy
              </Link>.
            </p>
          </motion.section>

          <motion.section
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
          >
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">8. Cancellation and Refunds</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300 transition-colors duration-300">
              <li>You can cancel your subscription at any time through your App Store account settings.</li>
              <li>Refunds are handled by Apple according to their policies and must be requested through the App Store.</li>
              <li>No refunds will be issued for partial subscription periods.</li>
            </ul>
          </motion.section>

          <motion.section
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
          >
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">9. Changes to Terms</h2>
            <p className="text-gray-600 dark:text-gray-300 transition-colors duration-300">
              We reserve the right to modify these terms at any time. We will notify users of any material changes via email or through the app.
            </p>
          </motion.section>

          <motion.section
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3 }}
          >
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">10. Contact Us</h2>
            <p className="text-gray-600 dark:text-gray-300 transition-colors duration-300">
              If you have any questions about these Terms of Use, please contact us at:{' '}
              <a href="mailto:doruk@ttstats.ch" className="text-[#1C7AD6] hover:text-[#1565C0] transition-colors duration-300">
                doruk@ttstats.ch
              </a>
            </p>
          </motion.section>
        </motion.div>
      </main>

      {/* Footer */}
      <UnifiedFooter variant="personal" />
    </div>
  );
};

export default TermsPage;